/**
 * HmApprovalSection — obieg akceptacji na formatce dokumentu z Symfonii Handel.
 *
 * Jeden komponent dla trzech kartotek (sprzedaż / magazyn / zamówienia). Domyka różnicę między
 * dokumentem naszym a cudzym: pozostałe formatki podają obiegowi identyfikator rekordu wprost,
 * bo rekord istnieje w bazie. Dokument Symfonii dostaje nasz identyfikator dopiero w chwili
 * wysyłania do akceptacji (rezerwacja), więc najpierw pytamy, czy taki identyfikator już jest.
 *
 * Dwa stany:
 *   • dokument powiązany → pełny baner akceptacji (stan wniosku, decyzje, anulowanie),
 *   • niepowiązany → sam przycisk „Wyślij do akceptacji", który rezerwuje identyfikator
 *     dopiero po kliknięciu. Inaczej każde otwarcie dokumentu zostawiałoby wiersz w bazie.
 *
 * Obieg wymaga OBU modułów naraz. Gdy tenant nie ma „Procesów", zapytanie odbija się o bramkę
 * (402) i sekcja po prostu się nie pokazuje — formatka dokumentu nie jest miejscem na komunikat
 * o braku subskrypcji innego modułu.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { HANDEL_TARGET_MODULE_CODE } from '@saas/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { SubmitForApprovalButton } from '@/components/processes/SubmitForApprovalButton';
import {
  type HmLinkKind,
  lookupDocumentLink,
  reserveDocumentLink,
} from '@/lib/handel-magazyn/document-links-api';

export interface HmApprovalSectionProps {
  kind: HmLinkKind;
  /** `Id` dokumentu w Symfonii. */
  externalId: number;
  /** Firma z aktywnego zakresu — `null`, póki nie jest wybrana (wtedy sekcji nie ma). */
  companyId: string | null;
  testIdPrefix?: string;
}

export function documentLinkQueryKey(
  kind: HmLinkKind,
  externalId: number,
  companyId: string | null,
): [string, string, HmLinkKind, number, string | null] {
  return ['handel-magazyn', 'document-link', kind, externalId, companyId];
}

export function HmApprovalSection({
  kind,
  externalId,
  companyId,
  testIdPrefix = 'hm-approval',
}: HmApprovalSectionProps): ReactElement | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const linkQuery = useQuery({
    queryKey: documentLinkQueryKey(kind, externalId, companyId),
    queryFn: () => lookupDocumentLink({ kind, externalId, companyId: companyId! }),
    enabled: !!companyId && Number.isInteger(externalId) && externalId > 0,
    // Brak modułu „Procesy" albo brak połączenia z Symfonią to nie jest stan do ponawiania.
    retry: false,
  });

  if (linkQuery.isPending || linkQuery.isError) return null;

  const link = linkQuery.data;

  if (link) {
    return (
      <ProcessApprovalBanner
        moduleCode={HANDEL_TARGET_MODULE_CODE}
        recordId={link.id}
        testIdPrefix={testIdPrefix}
      />
    );
  }

  return (
    <section
      data-testid={testIdPrefix}
      className="rounded border border-border-secondary bg-bg-secondary px-3 py-2"
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-text-primary">
          {t('tenant.processes.banner.title', 'Akceptacje')}
        </h3>
        <SubmitForApprovalButton
          moduleCode={HANDEL_TARGET_MODULE_CODE}
          recordId=""
          resolveRecordId={async () => {
            const reserved = await reserveDocumentLink({ kind, externalId, companyId: companyId! });
            // Podmieniamy wynik zapytania od razu — po starcie wniosku sekcja ma pokazać baner,
            // a nie przycisk „Wyślij" po raz drugi.
            queryClient.setQueryData(documentLinkQueryKey(kind, externalId, companyId), reserved);
            return reserved.id;
          }}
          onSubmitted={() => {
            void queryClient.invalidateQueries({
              queryKey: documentLinkQueryKey(kind, externalId, companyId),
            });
          }}
          testIdPrefix={testIdPrefix}
        />
      </div>
    </section>
  );
}
